/**
 * When a failed request is worth making again, and how long to wait first.
 *
 * Kept apart from the query wiring because it is the part with actual
 * judgement in it, and because a pure module can be exercised directly
 * without standing up React to do it.
 */
import { SlError } from './sl.ts';

/**
 * How many times a failed request is retried before the board admits defeat.
 *
 * Three, because the failures worth retrying are nearly all brief: a phone
 * changing cell, a dropped connection, a 5xx, or our own ten-second deadline
 * on a stalled request. Spread over the backoff below that covers about
 * fifteen seconds, which fits inside one refresh interval -- there is no
 * point still retrying when a fresh attempt is about to happen anyway.
 */
const MAX_RETRIES = 3;

/**
 * The longest a retry may sit waiting before it is someone else's problem.
 *
 * A retry that waits longer than the board's own refresh cycle buys nothing:
 * the scheduled refetch would have run first anyway, and in the meantime the
 * screen is empty with no explanation, because a query that is waiting to
 * retry has not failed yet and so reports nothing. A quota refusal carries a
 * 45-second cooldown, which is exactly that case -- far better to say "SL is
 * rationing, we will try again shortly" straight away and let the next
 * refresh do it.
 */
const MAX_RETRY_WAIT_MS = 10_000;

/** Only failures that could plausibly go differently in the next few seconds. */
export function shouldRetry(failureCount: number, error: unknown): boolean {
  if (failureCount > MAX_RETRIES) return false;
  if (!(error instanceof SlError) || !error.retryable) return false;
  return (error.retryAfterMs ?? 0) <= MAX_RETRY_WAIT_MS;
}

/**
 * Exponential backoff with jitter: roughly 1s, 2s, 4s.
 *
 * The jitter is not decoration. Every stop on the board fetches at the same
 * moment, so when SL is unhappy they all fail at the same moment too, and
 * without a random spread they would then retry in lockstep -- arriving as
 * one burst, which is exactly what a quota limit punishes. A +/-30% spread
 * pulls them apart.
 *
 * A server that names its own wait is obeyed rather than guessed at -- though
 * only waits short enough to be worth holding the query open reach here at
 * all; see MAX_RETRY_WAIT_MS.
 */
export function retryDelay(attempt: number, error: unknown): number {
  const asked = error instanceof SlError ? error.retryAfterMs : null;
  const base = Math.min(1000 * 2 ** attempt, 15_000);
  const jittered = Math.round(base * (0.7 + Math.random() * 0.6));
  return Math.max(asked ?? 0, jittered);
}
